import './i18n/autoTranslate';
import React, { useState } from 'react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { Loader2, Wand2, Copy, Download, MessageSquare, User, Bot, BarChart3, Lock, CheckCircle2, Sparkles, ShieldCheck, Volume2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AppBackground from '../components/ui/AppBackground.tsx';
import SpotlightCard from '../components/ui/SpotlightCard';
import { jsPDF } from 'jspdf'; 
import Confetti from 'react-confetti'
import { INTERVIEW_API_BASE_URL, getAccessToken, getAuthHeaders } from '../config/api';

export default function GenerateQuestionsPage() {
  const [jobRole, setJobRole] = useState('');
  const [difficulty, setDifficulty] = useState('beginner');
  const [industry, setIndustry] = useState('technology');
  const [interviewType, setInterviewType] = useState('mixed');
  const [questionCount, setQuestionCount] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState(null);
  const [interviewMeta, setInterviewMeta] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisCompleted, setAnalysisCompleted] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showConfetti,setShowConfetti]=useState(false);

  const playQuestionAudio = (audioUrl) => {
    if (!audioUrl) return;
    const audio = new Audio(audioUrl);
    audio.play().catch((error) => console.error('Question audio playback failed:', error));
  };
  const [userAnswers, setUserAnswers] = useState({});
  const { toast } = useToast();

  async function onSubmit(e) {
    e.preventDefault();
    setIsLoading(true);
    setGeneratedQuestions(null);
    setInterviewMeta(null);
    setAnalysis(null);
    setAnalysisCompleted(false);
    setUserAnswers({});

    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error('Login required to generate AI questions');
      }

      const difficultyLevel =
        difficulty === "advanced" ? 4 : difficulty === "beginner" ? 2 : 3;

      const createResponse = await fetch(`${INTERVIEW_API_BASE_URL}/create`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          selectedFormat: "chat",
          duration: 30,
          selectedIndustry: industry,
          selectedRole: jobRole,
          interviewType,
          personality: {
            selectedPersona: { id: "professional", name: "Professional" },
            accent: "american",
            tone: 3,
            difficulty: difficultyLevel,
            pace: 3,
            pressure: 3,
          },
          enableFollowUps: false,
          surpriseQuestions: false,
          starMethodEnforcement: false,
          followUpIntensity: 3,
          adaptiveDifficulty: false,
          customQuestions: [],
          selectedCompanies: [],
          selectedCategories: [],
        })
      });

      const createText = await createResponse.text();
      let createData = null;
      try {
        createData = JSON.parse(createText);
      } catch {
        createData = null;
      }

      if (!createResponse.ok) {
        throw new Error(`API error: ${createResponse.status} - ${createText}`);
      }

      setInterviewMeta({
        sessionId: createData?.session_id,
        interviewId: createData?.interview_id,
      });
      if (createData?.session_id) localStorage.setItem("current_session_id", createData.session_id);
      if (createData?.interview_id) localStorage.setItem("current_interview_id", String(createData.interview_id));

      const response = await fetch(`${INTERVIEW_API_BASE_URL}/generate-questions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          questionCount,
          interviewType,
          jobRole,
          industry,
          difficulty,
          personaStyle: "professional",
          tone: 3,
          difficulty_level: difficultyLevel,
          starMethodEnforcement: false,
          enableFollowUps: false,
        })
      });

      const responseText = await response.text();
      let data = null;
      try {
        data = JSON.parse(responseText);
      } catch {
        data = null;
      }

      const rawItems = Array.isArray(data?.question_items) && data.question_items.length
        ? data.question_items
        : (Array.isArray(data?.questions) ? data.questions : []);

      const questionItems = rawItems.map((item, index) => ({
        id: item?.id || index + 1,
        question: String((typeof item === 'object' ? item?.question : item) || ''),
        audio_url: item?.audio_url || item?.audioUrl || '',
        audio_status: item?.audio_status || '',
      })).filter((item) => item.question);

      const questionsList = questionItems.map((item) => item.question);

      if (!questionsList.length) {
        if (!response.ok) {
          throw new Error(`API error: ${response.status} - ${responseText}`);
        }
        throw new Error("No questions returned from the server.");
      }

      setGeneratedQuestions({ questions: questionsList, questionItems });
      toast({
        title: "Questions Generated",
        description: !response.ok || data?.used_fallback
          ? "Questions generated using fallback (AI temporarily unavailable)."
          : "Your interview questions are ready!",
      });
    } catch (error) {
      console.error(error);
      toast({
        variant: "destructive",
        title: "Failed to generate questions",
        description: error?.message || "There was an error generating the questions. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleAnswerChange = (index, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [index]: answer
    }));
  };

  const estimateMetrics = (text) => {
    const normalized = (text || "").trim();
    const wordCount = normalized ? normalized.split(/\s+/).filter(Boolean).length : 0;
    const lengthScore = Math.min(100, Math.round((wordCount / 120) * 100));
    const hasStructure = /(\bfirst\b|\bsecond\b|\bfinally\b|\bfor example\b|\bfor instance\b)/i.test(normalized);
    const hasDetails = /(\d+|%|\bmonths?\b|\byears?\b|\busers?\b|\bmetrics?\b)/i.test(normalized);
    const clarity = Math.max(10, Math.min(100, lengthScore + (hasStructure ? 10 : 0) + (hasDetails ? 10 : 0)));
    const professionalism = Math.max(10, Math.min(100, 55 + (hasStructure ? 10 : 0) + Math.round(wordCount / 10)));
    const tone = Math.max(10, Math.min(100, 60 + Math.round(wordCount / 12)));
    const pacing = Math.max(10, Math.min(100, 60 + Math.round(wordCount / 15)));
    const response_time = 85;
    return { clarity, pacing, response_time, professionalism, tone };
  };

  const estimateDurationSeconds = (text) => {
    const normalized = (text || "").trim();
    const wordCount = normalized ? normalized.split(/\s+/).filter(Boolean).length : 0;
    const seconds = Math.round(Math.max(20, Math.min(240, (wordCount / 130) * 60)));
    return seconds;
  };

  const allAnswersProvided = () => {
    const qs = generatedQuestions?.questions || [];
    if (!qs.length) return false;
    return qs.every((_, idx) => (userAnswers[idx] || "").trim().length > 0);
  };

  const normalizeResultsItems = (items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((it) => (typeof it === "string" ? it : it?.text || it?.value || ""))
      .map((it) => String(it).trim())
      .filter(Boolean);
  };

  const getEffectiveInterviewId = () => {
    const fromState = Number(interviewMeta?.interviewId);
    if (Number.isFinite(fromState) && fromState > 0) return fromState;
    const fromStorage = Number(localStorage.getItem("current_interview_id"));
    if (Number.isFinite(fromStorage) && fromStorage > 0) return fromStorage;
    return null;
  };

  const submitResultsToBackend = async (analysisData, answersPayload) => {
    const interviewId = getEffectiveInterviewId();
    if (!interviewId) {
      throw new Error("No interview id found to submit results.");
    }

    const finalScoreValue =
      analysisData?.final_score ??
      analysisData?.ai_analysis?.final_score ??
      analysisData?.local_analysis?.final_score ??
      0;
    const score = Math.round(Number(finalScoreValue) || 0);

    const avg = analysisData?.local_analysis?.statistics?.average_metrics || {};
    const performance_categories = {
      clarity: Math.round(Number(avg?.clarity) || 0),
      pacing: Math.round(Number(avg?.pacing) || 0),
      professionalism: Math.round(Number(avg?.professionalism) || 0),
      tone: Math.round(Number(avg?.tone) || 0),
      response_time: Math.round(Number(avg?.response_time) || 0),
    };

    const strengths = normalizeResultsItems(
      analysisData?.ai_analysis?.detailed_strengths || analysisData?.local_analysis?.insights?.strengths
    );
    const weakest_areas = normalizeResultsItems(
      analysisData?.ai_analysis?.detailed_weaknesses || analysisData?.local_analysis?.insights?.areas_for_improvement
    );
    const improvement_suggestions = normalizeResultsItems(
      analysisData?.ai_analysis?.actionable_recommendations || analysisData?.local_analysis?.insights?.recommendations
    );
    const detailed_feedback = Array.isArray(analysisData?.ai_analysis?.answer_specific_feedback)
      ? analysisData.ai_analysis.answer_specific_feedback
      : null;

    const next_steps = {
      items: normalizeResultsItems(analysisData?.ai_analysis?.next_steps),
    };

    const speaking_pace_wpm = Number(analysisData?.local_analysis?.statistics?.average_words_per_minute);
    const filler_word_count = Number(analysisData?.local_analysis?.statistics?.total_filler_words);

    const payload = {
      overall_score: score,
      performance_score: score,
      feedback: String(analysisData?.ai_analysis?.overall_assessment || ""),
      performance_categories,
      strengths,
      weakest_areas,
      detailed_feedback,
      answer_reviews: answersPayload.map((a) => ({
        question: a.question,
        transcript: a.transcript,
        duration: a.duration,
        metrics: a.metrics,
      })),
      improvement_suggestions,
      next_steps,
      speaking_pace_wpm: Number.isFinite(speaking_pace_wpm) ? speaking_pace_wpm : null,
      filler_word_count: Number.isFinite(filler_word_count) ? filler_word_count : null,
      star_method_usage_count: null,
      total_questions_asked: answersPayload.length,
    };

    const putRes = await fetch(`${INTERVIEW_API_BASE_URL}/${interviewId}/results`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const putText = await putRes.text();
    let putData = null;
    try {
      putData = JSON.parse(putText);
    } catch {
      putData = null;
    }
    if (!putRes.ok) {
      throw new Error(`Results submission failed: ${putRes.status} - ${putText}`);
    }

    const getRes = await fetch(`${INTERVIEW_API_BASE_URL}/${interviewId}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    const getText = await getRes.text();
    let getData = null;
    try {
      getData = JSON.parse(getText);
    } catch {
      getData = null;
    }
    if (!getRes.ok) {
      throw new Error(`Failed to verify interview update: ${getRes.status} - ${getText}`);
    }

    setInterviewMeta((prev) => ({
      ...(prev || {}),
      interviewId,
      sessionId: prev?.sessionId || localStorage.getItem("current_session_id") || null,
      status: getData?.interview?.status || null,
      results: getData?.interview?.results || null,
    }));

    return { putData, getData };
  };

  const handleAnalyze = async () => {
    const token = getAccessToken();
    if (!token) {
      toast({
        variant: "destructive",
        title: "Login required",
        description: "Please login to analyze your answers.",
      });
      return;
    }

    if (!generatedQuestions?.questions?.length) return;
    if (!allAnswersProvided()) {
      toast({
        variant: "destructive",
        title: "Answers missing",
        description: "Please answer all questions before analyzing.",
      });
      return;
    }

    setIsAnalyzing(true);
    setAnalysis(null);
    setAnalysisCompleted(false);

    try {
      const answers = generatedQuestions.questions.map((q, idx) => {
        const transcript = String(userAnswers[idx] || "");
        return {
          question: String(q),
          transcript,
          duration: estimateDurationSeconds(transcript),
          metrics: estimateMetrics(transcript),
        };
      });

      const response = await fetch(`${INTERVIEW_API_BASE_URL}/final-analysis`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          answers,
          fillerWords: {},
          use_ai: true,
        }),
      });

      const responseText = await response.text();
      let data = null;
      try {
        data = JSON.parse(responseText);
      } catch {
        data = null;
      }

      if (!response.ok || !data) {
        throw new Error(`API error: ${response.status} - ${responseText}`);
      }

      setAnalysis(data);
      await submitResultsToBackend(data, answers);
      setAnalysisCompleted(true);
      toast({
        title: "Analysis Complete",
        description: "Your interview results were saved successfully.",
      });
    } catch (error) {
      console.error(error);
      toast({
        variant: "destructive",
        title: "Analysis failed",
        description: error?.message || "There was an error analyzing your answers. Please try again.",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const copyToClipboard = () => {
    if (!generatedQuestions?.questions?.length) return;
    if (!analysisCompleted) {
      toast({
        variant: "destructive",
        title: "Analyze first",
        description: "Complete analysis to unlock copy/export.",
      });
      return;
    }
    const textToCopy = generatedQuestions.questions
      .map((q, i) => `${i + 1}. ${q}\n\nAnswer: ${userAnswers[i] || '[Your answer here]'}\n`)
      .join('\n');
    
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        toast({
          title: "Copied to clipboard",
          description: "All questions and answers have been copied.",
        });
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        toast({
          variant: "destructive",
          title: "Failed to copy",
          description: "Could not copy to clipboard.",
        });
      });
  };


  const downloadAsPDF = async () => {
    if (!generatedQuestions?.questions?.length) return;
    if (!analysisCompleted) {
      toast({
        variant: "destructive",
        title: "Analyze first",
        description: "Complete analysis to unlock PDF export.",
      });
      return;
    }
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 14;
    const headerHeight = 34;
    const currentDate = new Date().toLocaleDateString();
    const primary = { r: 139, g: 124, b: 255 };
    const primary2 = { r: 108, g: 99, b: 255 };
    const cyan = { r: 34, g: 211, b: 238 };

    const normalizePdfList = (items) => {
      if (!Array.isArray(items)) return [];
      return items
        .map((it) => (typeof it === "string" ? it : it?.text || it?.value || ""))
        .map((it) => String(it).trim())
        .filter(Boolean);
    };

    const analysisFinalScore =
      analysis?.final_score ??
      analysis?.ai_analysis?.final_score ??
      analysis?.local_analysis?.final_score ??
      0;
    const analysisMetrics = analysis?.local_analysis?.statistics?.average_metrics || {};
    const pdfStrengths = normalizePdfList(analysis?.ai_analysis?.detailed_strengths || analysis?.local_analysis?.insights?.strengths);
    const pdfImprovements = normalizePdfList(analysis?.ai_analysis?.detailed_weaknesses || analysis?.local_analysis?.insights?.areas_for_improvement);
    const pdfRecommendations = normalizePdfList(analysis?.ai_analysis?.actionable_recommendations || analysis?.local_analysis?.insights?.recommendations);
    const pdfAssessment = String(analysis?.ai_analysis?.overall_assessment || "").trim();

    const drawHeader = (pageNumber, totalPages = null) => {
      doc.setFillColor(11, 11, 18);
      doc.rect(0, 0, pageWidth, headerHeight, "F");
      doc.setFillColor(primary2.r, primary2.g, primary2.b);
      doc.rect(0, headerHeight, pageWidth, 1.2, "F");
      doc.setFillColor(cyan.r, cyan.g, cyan.b);
      doc.rect(0, headerHeight + 1.2, pageWidth, 0.6, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("InterviewGenieAI", marginX, 18);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(220, 220, 230);
      doc.text("AI-Powered Interview Preparation", marginX, 26);

      const rightText = totalPages ? `Page ${pageNumber}/${totalPages}` : `Page ${pageNumber}`;
      doc.setTextColor(210, 210, 220);
      doc.text(rightText, pageWidth - marginX, 18, { align: "right" });
      doc.text(currentDate, pageWidth - marginX, 26, { align: "right" });
    };

    const drawFooter = (pageNumber, totalPages) => {
      doc.setTextColor(150, 150, 160);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Confidential - InterviewGenieAI © ${new Date().getFullYear()}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );
      doc.text(`${pageNumber}/${totalPages}`, pageWidth - marginX, pageHeight - 10, { align: "right" });
    };

    const startNewPage = (pageNumber, totalPages = null) => {
      if (pageNumber > 1) doc.addPage();
      drawHeader(pageNumber, totalPages);
      return headerHeight + 16;
    };

    const ensureSpace = (y, needed, pageNumberRef, totalPages = null) => {
      if (y + needed <= pageHeight - 18) return { y, pageNumberRef };
      pageNumberRef.value += 1;
      const newY = startNewPage(pageNumberRef.value, totalPages);
      return { y: newY, pageNumberRef };
    };

    const pageNumberRef = { value: 1 };
    let y = startNewPage(1);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    const title = "Interview Practice Report";
    doc.text(title, marginX, y);
    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(220, 220, 230);
    const metaLeft = [
      `Role: ${jobRole || "—"}`,
      `Type: ${interviewType || "—"}`,
      `Industry: ${industry || "—"}`,
      `Difficulty: ${difficulty || "—"}`,
    ];
    const interviewIdText = interviewMeta?.interviewId ? `Interview ID: ${interviewMeta.interviewId}` : null;
    const sessionIdText = interviewMeta?.sessionId ? `Session ID: ${interviewMeta.sessionId}` : null;
    const metaRight = [interviewIdText, sessionIdText].filter(Boolean);

    metaLeft.forEach((line) => {
      y += 6;
      doc.text(line, marginX, y);
    });
    let yRight = headerHeight + 16 + 8;
    metaRight.forEach((line) => {
      yRight += 6;
      doc.text(line, pageWidth - marginX, yRight, { align: "right" });
    });
    y += 10;

    ({ y } = ensureSpace(y, 56, pageNumberRef));
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(marginX, y, pageWidth - marginX * 2, 48, 6, 6, "F");
    doc.setFillColor(primary2.r, primary2.g, primary2.b);
    doc.roundedRect(marginX, y, 4, 48, 6, 6, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 46);
    doc.text("Results", marginX + 10, y + 14);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(26);
    doc.setTextColor(primary2.r, primary2.g, primary2.b);
    doc.text(String(Math.round(Number(analysisFinalScore) || 0)), marginX + 10, y + 36);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(70, 70, 80);
    doc.text("Final Score", marginX + 40, y + 26);
    doc.text("0–100", marginX + 40, y + 34);

    y += 60;

    const metricItems = [
      { label: "Clarity", value: analysisMetrics?.clarity },
      { label: "Pacing", value: analysisMetrics?.pacing },
      { label: "Professionalism", value: analysisMetrics?.professionalism },
      { label: "Tone", value: analysisMetrics?.tone },
      { label: "Response Time", value: analysisMetrics?.response_time },
    ];

    const boxGap = 4;
    const boxW = (pageWidth - marginX * 2 - boxGap * 4) / 5;
    const boxH = 18;
    ({ y } = ensureSpace(y, boxH + 10, pageNumberRef));
    metricItems.forEach((m, idx) => {
      const x = marginX + idx * (boxW + boxGap);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(x, y, boxW, boxH, 4, 4, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(30, 30, 46);
      doc.text(m.label, x + 3, y + 7);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(primary.r, primary.g, primary.b);
      doc.text(String(Math.round(Number(m.value) || 0)), x + 3, y + 15);
    });
    y += boxH + 10;

    if (pdfAssessment) {
      const assessmentLines = doc.splitTextToSize(pdfAssessment, pageWidth - marginX * 2 - 12);
      ({ y } = ensureSpace(y, assessmentLines.length * 5 + 18, pageNumberRef));
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(marginX, y, pageWidth - marginX * 2, assessmentLines.length * 5 + 12, 6, 6, "F");
      doc.setFillColor(cyan.r, cyan.g, cyan.b);
      doc.roundedRect(marginX, y, 4, assessmentLines.length * 5 + 12, 6, 6, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 46);
      doc.text("Overall Assessment", marginX + 10, y + 10);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 70);
      doc.text(assessmentLines, marginX + 10, y + 18);
      y += assessmentLines.length * 5 + 18;
    }

    const threeColumns = [
      { title: "Strengths", color: { r: 16, g: 185, b: 129 }, items: pdfStrengths },
      { title: "Improve", color: { r: 245, g: 158, b: 11 }, items: pdfImprovements },
      { title: "Recommendations", color: { r: 56, g: 189, b: 248 }, items: pdfRecommendations },
    ];

    const colGap = 6;
    const colW = (pageWidth - marginX * 2 - colGap * 2) / 3;
    const listMax = 5;
    const colBaseH = 12 + listMax * 6 + 10;
    ({ y } = ensureSpace(y, colBaseH + 10, pageNumberRef));
    threeColumns.forEach((c, idx) => {
      const x = marginX + idx * (colW + colGap);
      const items = (c.items?.length ? c.items : ["—"]).slice(0, listMax);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(x, y, colW, colBaseH, 6, 6, "F");
      doc.setFillColor(c.color.r, c.color.g, c.color.b);
      doc.roundedRect(x, y, 4, colBaseH, 6, 6, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 46);
      doc.text(c.title, x + 8, y + 12);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 70);
      items.forEach((t, i) => {
        doc.text(`• ${t}`, x + 8, y + 20 + i * 6, { maxWidth: colW - 14 });
      });
    });
    y += colBaseH + 12;

    ({ y } = ensureSpace(y, 14, pageNumberRef));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text("Questions & Answers", marginX, y);
    y += 6;

    generatedQuestions.questions.forEach((question, index) => {
      const answer = String(userAnswers[index] || "").trim();
      const questionLines = doc.splitTextToSize(`${index + 1}. ${String(question)}`, pageWidth - marginX * 2 - 18);
      const answerLines = doc.splitTextToSize(answer ? `Answer: ${answer}` : "Answer: —", pageWidth - marginX * 2 - 18);
      const cardH = 14 + questionLines.length * 5 + 6 + answerLines.length * 5 + 10;
      ({ y } = ensureSpace(y, cardH + 6, pageNumberRef));

      doc.setFillColor(255, 255, 255);
      doc.roundedRect(marginX, y, pageWidth - marginX * 2, cardH, 8, 8, "F");
      doc.setFillColor(primary2.r, primary2.g, primary2.b);
      doc.roundedRect(marginX, y, 4, cardH, 8, 8, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 46);
      doc.text(questionLines, marginX + 10, y + 12);

      const answerY = y + 14 + questionLines.length * 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 70);
      doc.text(answerLines, marginX + 10, answerY + 6);

      y += cardH + 6;
    });

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      drawHeader(i, totalPages);
      drawFooter(i, totalPages);
    }
    
    // Save with professional filename
    const formattedJobRole = jobRole.toLowerCase().replace(/\s+/g, '-');
    const formattedDifficulty = difficulty.toLowerCase();
    doc.save(`InterviewGenieAI-${formattedJobRole}-${formattedDifficulty}-questions.pdf`);
    
    toast({
      title: "PDF Downloaded Successfully",
      description: "Your professional interview questions PDF is ready.",
    });
    
    setShowConfetti(true);
    setTimeout(() => {
      setShowConfetti(false);
    }, 6000);
  };
  
  const difficultyOptions = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const interviewTypeOptions = [
    { value: 'mixed', label: 'Mixed (Recommended)' },
    { value: 'behavioral', label: 'Behavioral' },
    { value: 'technical', label: 'Technical' },
    { value: 'situational', label: 'Situational' },
    { value: 'company_specific', label: 'Company-Specific' }
  ];

  const industryOptions = [
    { value: 'technology', label: 'Technology' },
    { value: 'finance', label: 'Finance & Banking' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'marketing', label: 'Marketing & Advertising' },
    { value: 'education', label: 'Education' }
  ];

  const questionCountOptions = [
    { value: 5, label: '5 Questions' },
    { value: 10, label: '10 Questions' }
  ];

  const hasQuestions = Boolean(generatedQuestions?.questions?.length);
  const stepIndex = analysisCompleted ? 3 : hasQuestions ? 2 : 1;

  const normalizeList = (items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((it) => (typeof it === "string" ? it : it?.text || it?.value || ""))
      .map((it) => String(it).trim())
      .filter(Boolean);
  };

  const finalScore =
    analysis?.final_score ??
    analysis?.ai_analysis?.final_score ??
    analysis?.local_analysis?.final_score ??
    0;

  const averageMetrics = analysis?.local_analysis?.statistics?.average_metrics || {};
  const strengths = normalizeList(analysis?.ai_analysis?.detailed_strengths || analysis?.local_analysis?.insights?.strengths);
  const improvements = normalizeList(analysis?.ai_analysis?.detailed_weaknesses || analysis?.local_analysis?.insights?.areas_for_improvement);
  const recommendations = normalizeList(analysis?.ai_analysis?.actionable_recommendations || analysis?.local_analysis?.insights?.recommendations);
  const overallAssessment = analysis?.ai_analysis?.overall_assessment || "";
  return (
    <AppBackground className="min-h-screen relative  text-white overflow-hidden">
      {/* TechApp-style ambient background */}
      {/* <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-[80px]" />
        <div className="absolute -bottom-28 -right-24 h-[560px] w-[560px] rounded-full bg-sky-400/10 blur-[90px]" />
        <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_65%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
        <div className="absolute inset-0 opacity-35 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px]" />
        <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:92px_92px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/15 to-black/70" />
      </div> */}

      <main className="relative z-10 pt-10 pb-24">
        {showConfetti && <Confetti width={1500} height={800} />}

        <div className="max-w-full mx-2 px-4 sm:px-6 lg:px-8 space-y-6">
          {/* Hero (Questions Generator) */}
          <div className="relative overflow-hidden px-6   sm:px-10 sm:py-14">
          
          
            <div className="relative mx-auto max-w-3xl text-center">
              {/* Badge */}
              <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70 shadow-[0_0_0_1px_rgba(255,255,255,0.03)] backdrop-blur-xl">
                <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_16px_rgba(52,211,153,0.65)]" />
                AI Question Generator • Role-based • Difficulty-aware • Instant output
                <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/70">
                  Smart cache
                </span>
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-white/70">
                  Export-ready
                </span>
              </div>
          
              {/* Title */}
              <h1 className="mt-5 text-4xl sm:text-6xl font-semibold tracking-tight leading-[1.03] text-white">
                Generate interview questions that feel real.
                <span className="block mt-3 text-xl sm:text-3xl text-transparent bg-clip-text bg-gradient-to-r from-violet-200 via-sky-200 to-emerald-200">
                  Precision prompts, premium structure, and a clean workflow → in one flow.
                </span>
              </h1>
          
              {/* Supporting text */}
              <p className="mx-auto mt-5 max-w-2xl text-sm sm:text-base text-white/65 leading-relaxed">
                Configure role + level + interview style, then get a polished set of questions with consistent formatting,
                clear objectives, and ready-to-use output for your AI interview session.
              </p>
          
              {/* Mini trust row */}
              <div className="mt-7 flex flex-wrap items-center justify-center gap-2 text-[11px] text-white/55">
                <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 backdrop-blur-xl">
                  Consistent formatting
                </span>
                <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 backdrop-blur-xl">
                  Balanced difficulty
                </span>
                <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 backdrop-blur-xl">
                  Ready for scoring
                </span>
              </div>
            </div>
          </div>

          {/* Top Header */}
          <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)] overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-emerald-400/10 blur-[70px]" />
              <div className="absolute -bottom-40 right-0 h-80 w-80 rounded-full bg-sky-400/10 blur-[80px]" />
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-transparent" />
            </div>

            <div className="relative p-6 sm:p-7">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-2xl bg-white/8 border border-white/12 backdrop-blur-xl flex items-center justify-center shadow-[0_0_25px_rgba(52,211,153,0.18)]">
                    <Sparkles className="h-5 w-5 text-white/90" />
                  </div>
                  <div className="leading-tight">
                    <div className="text-xl sm:text-2xl font-semibold tracking-tight">Generate Questions</div>
                    <div className="text-sm text-white/60">
                      Configure → answer → analyze → export. Clean, fast, professional.
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-semibold tracking-wide text-emerald-100">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Secure session
                  </span>

                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide ${
                    stepIndex >= 1 ? "border-white/12 bg-white/6 text-white/80" : "border-white/10 bg-white/4 text-white/60"
                  }`}>
                    1 · Configure
                  </span>
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide ${
                    stepIndex >= 2 ? "border-white/12 bg-white/6 text-white/80" : "border-white/10 bg-white/4 text-white/60"
                  }`}>
                    2 · Answer
                  </span>
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide ${
                    stepIndex >= 3 ? "border-white/12 bg-white/6 text-white/80" : "border-white/10 bg-white/4 text-white/60"
                  }`}>
                    3 · Results
                  </span>

                  {interviewMeta?.interviewId && (
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/6 px-3 py-1 text-[11px] font-semibold tracking-wide text-white/80">
                      <span className="text-white/55">Interview</span>
                      <span className="text-white">{interviewMeta.interviewId}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-5 h-2 rounded-full bg-white/8 overflow-hidden border border-white/10">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-400/70 via-sky-400/70 to-white/60"
                  style={{ width: `${stepIndex === 1 ? 28 : stepIndex === 2 ? 68 : 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Main layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Configuration */}
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)] overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute -top-24 -left-24 h-64 w-64 rounded-full bg-emerald-400/10 blur-[70px]" />
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.05] via-transparent to-transparent" />
                </div>

                <div className="relative p-6">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white/8 border border-white/12">
                        <Wand2 size={18} className="text-white/85" />
                      </div>
                      <div>
                        <div className="text-base font-semibold tracking-tight">Configuration</div>
                        <div className="text-sm text-white/60">Set the target role and generate a curated set.</div>
                      </div>
                    </div>

                    <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/6 px-3 py-1 text-[11px] font-semibold text-white/75">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      Ready
                    </span>
                  </div>

                  <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Job Role"
                      placeholder="e.g., Full Stack Developer"
                      value={jobRole}
                      onChange={(e) => setJobRole(e.target.value)}
                      className="bg-white/5 border-white/10"
                    />

                    <Select
                      label="Interview Type"
                      options={interviewTypeOptions}
                      value={interviewType}
                      onChange={setInterviewType}
                      placeholder="Select interview type"
                    />

                    <Select
                      label="Industry"
                      options={industryOptions}
                      value={industry}
                      onChange={setIndustry}
                      placeholder="Select industry"
                    />

                    <Select
                      label="Difficulty Level"
                      options={difficultyOptions}
                      value={difficulty}
                      onChange={setDifficulty}
                      placeholder="Select difficulty"
                    />

                    <Select
                      label="Question Count"
                      options={questionCountOptions}
                      value={questionCount}
                      onChange={(val) => setQuestionCount(Number(val))}
                      placeholder="Select count"
                    />

                    <div className="flex items-end">
                      <Button
                        type="submit"
                        fullWidth
                        loading={isLoading}
                        disabled={isLoading || !jobRole?.trim()}
                      >
                        Generate Questions
                      </Button>
                    </div>
                  </form>

                  {/* Pro tip */}
                  <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
                    <div className="flex items-start gap-3">
                      <div className=" flex items-center justify-center h-9 w-12 rounded-xl bg-emerald-400/10 border border-emerald-400/15 text-emerald-100">
                        <Sparkles className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-white/85">Pro tip</div>
                        <div className="mt-1 leading-relaxed">
                          Write answers with structure (context → action → result). Analysis becomes sharper, and your export looks professional.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Questions + Answers */}
            <div className="lg:col-span-7">
              <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)] overflow-hidden min-h-[560px]">
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute -bottom-28 -right-24 h-72 w-72 rounded-full bg-sky-400/10 blur-[80px]" />
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.05] via-transparent to-transparent" />
                </div>

                <div className="relative p-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-base font-semibold tracking-tight">Interview Questions</div>
                      <div className="text-sm text-white/60">
                        {hasQuestions ? "Answer all questions, then run analysis to unlock export." : "Generate questions to start."}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button onClick={copyToClipboard} variant="outline" size="sm" disabled={!analysisCompleted}>
                        {!analysisCompleted ? <Lock size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
                        Copy
                      </Button>
                      <Button onClick={downloadAsPDF} variant="outline" size="sm" disabled={!analysisCompleted}>
                        {!analysisCompleted ? <Lock size={16} className="mr-2" /> : <Download size={16} className="mr-2" />}
                        PDF
                      </Button>
                    </div>
                  </div>

                  {isLoading && (
                    <div className="mt-10 flex items-center justify-center h-64">
                      <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4">
                        <Loader2 className="animate-spin text-white/80 h-5 w-5" />
                        <span className="text-sm text-white/70">Generating questions…</span>
                      </div>
                    </div>
                  )}

                  {!isLoading && !hasQuestions && (
                    <div className="mt-10 flex flex-col items-center justify-center h-64 text-center">
                      <MessageSquare className="h-14 w-14 text-white/35 mb-4" />
                      <p className="text-white/80 text-lg font-semibold">No questions yet</p>
                      <p className="text-white/55 text-sm mt-2 max-w-sm">
                        Generate a set to begin the interview practice. Your answers will be analyzed and saved to your interview results.
                      </p>
                    </div>
                  )}

                  {!isLoading && hasQuestions && (
                    <div className="mt-6 space-y-4">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/70">
                        <span className="inline-flex items-center rounded-full border border-white/12 bg-white/6 px-3 py-1 font-semibold">
                          {interviewType}
                        </span>
                        <span className="inline-flex items-center rounded-full border border-white/12 bg-white/6 px-3 py-1 font-semibold">
                          {industry}
                        </span>
                        <span className="inline-flex items-center rounded-full border border-white/12 bg-white/6 px-3 py-1 font-semibold">
                          {difficulty}
                        </span>
                        <span className="inline-flex items-center rounded-full border border-white/12 bg-white/6 px-3 py-1 font-semibold">
                          {generatedQuestions.questions.length} questions
                        </span>
                        {!analysisCompleted && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/20 bg-amber-300/10 px-3 py-1 font-semibold text-amber-100">
                            <Lock className="h-3.5 w-3.5" />
                            Export locked
                          </span>
                        )}
                        {analysisCompleted && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 font-semibold text-emerald-100">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Export unlocked
                          </span>
                        )}
                      </div>

                      <div className="space-y-4 max-h-[560px] overflow-y-auto pr-2 [scrollbar-width:thin]">
                        {generatedQuestions.questions.map((question, index) => {
                          const answered = Boolean((userAnswers[index] || "").trim());
                          const audioUrl = generatedQuestions?.questionItems?.[index]?.audio_url || '';
                          return (
                            <div
                              key={index}
                              className="relative rounded-3xl border border-white/10 bg-white/[0.035] backdrop-blur-2xl overflow-hidden shadow-[0_16px_40px_-26px_rgba(0,0,0,0.8)]"
                            >
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-400/70 via-sky-400/70 to-white/40" />
                              <div className="absolute inset-0 pointer-events-none opacity-70">
                                <div className="absolute inset-0 bg-[radial-gradient(900px_300px_at_20%_0%,rgba(255,255,255,0.08),transparent_60%)]" />
                              </div>

                              <div className="relative p-5">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex items-start gap-3 min-w-0">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white/8 border border-white/12 text-white/85 shrink-0">
                                      <Bot size={18} />
                                    </div>
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <div className="text-sm font-semibold text-white/90">Question {index + 1}</div>
                                        <span
                                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                                            answered
                                              ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-100"
                                              : "border-white/12 bg-white/6 text-white/70"
                                          }`}
                                        >
                                          {answered ? "Answered" : "Pending"}
                                        </span>
                                      </div>
                                      <div className="mt-1 text-sm text-white/80 leading-relaxed">{question}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {audioUrl && (
                                      <button
                                        type="button"
                                        onClick={() => playQuestionAudio(audioUrl)}
                                        className="inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-white/10 bg-white/8 text-white/80 hover:bg-white/12 transition"
                                        title="Play ElevenLabs audio"
                                      >
                                        <Volume2 size={16} />
                                      </button>
                                    )}
                                    {answered && <CheckCircle2 size={18} className="text-emerald-400" />}
                                  </div>
                                </div>

                                <div className="mt-4 flex items-start gap-3">
                                  <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white/5 border border-white/10 text-white/70 shrink-0">
                                    <User size={18} />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-[11px] font-semibold text-white/60 mb-2">Your answer</div>
                                    <textarea
                                      value={userAnswers[index] || ""}
                                      onChange={(e) => handleAnswerChange(index, e.target.value)}
                                      placeholder="Write a structured answer (context, actions, outcomes)."
                                      className="w-full bg-black/25 border border-white/10 rounded-2xl p-3 min-h-[130px] text-white/90 placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-white/10"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                        <div className="text-xs text-white/55">
                          {analysisCompleted ? "Export unlocked." : "Complete analysis to unlock copy/export."}
                        </div>
                        <Button
                          onClick={handleAnalyze}
                          disabled={isAnalyzing || !hasQuestions || !allAnswersProvided()}
                          loading={isAnalyzing}
                          iconName={null}
                        >
                          <BarChart3 size={16} className="mr-2" />
                          Analyze Answers
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          {analysisCompleted && analysis && (
            <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)] overflow-hidden">
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-emerald-400/10 blur-[80px]" />
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.05] via-transparent to-transparent" />
              </div>

              <div className="relative p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white/8 border border-white/12">
                      <BarChart3 size={18} className="text-white/85" />
                    </div>
                    <div>
                      <div className="text-base font-semibold tracking-tight">Results</div>
                      <div className="text-sm text-white/60">Summary of your answers and recommendations.</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-xs text-white/55">Overall score</div>
                    <div className="text-2xl font-semibold text-white">
                      {Math.round(Number(finalScore) || 0)}
                      <span className="text-white/35 text-sm font-semibold"> /100</span>
                    </div>
                  </div>
                </div>

                {overallAssessment && (
                  <div className="mt-5 rounded-3xl border border-white/10 bg-white/5 p-5 text-white/75 leading-relaxed">
                    {overallAssessment}
                  </div>
                )}

                {/* Metrics */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-3">
                  {[
                    { label: "Clarity", value: averageMetrics.clarity, tone: "emerald" },
                    { label: "Pacing", value: averageMetrics.pacing, tone: "sky" },
                    { label: "Professionalism", value: averageMetrics.professionalism, tone: "neutral" },
                    { label: "Tone", value: averageMetrics.tone, tone: "sky" },
                    { label: "Response Time", value: averageMetrics.response_time, tone: "amber" },
                  ].map((m) => {
                    const v = Math.round(Number(m.value) || 0);
                    const barCls =
                      m.tone === "emerald"
                        ? "from-emerald-400/70 to-emerald-200/50"
                        : m.tone === "sky"
                        ? "from-sky-400/70 to-cyan-200/50"
                        : m.tone === "amber"
                        ? "from-amber-300/70 to-orange-200/50"
                        : "from-white/60 to-white/20";

                    return (
                      <div key={m.label} className="rounded-3xl border border-white/10 bg-white/5 p-4">
                        <div className="text-[11px] font-semibold text-white/60">{m.label}</div>
                        <div className="mt-1 text-2xl font-semibold text-white">{v}</div>
                        <div className="mt-3 h-2 rounded-full bg-white/8 overflow-hidden border border-white/10">
                          <div className={`h-full rounded-full bg-gradient-to-r ${barCls}`} style={{ width: `${Math.max(0, Math.min(100, v))}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Lists */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
                    <div className="text-sm font-semibold">Strengths</div>
                    <div className="mt-3 space-y-2 text-sm text-white/75">
                      {(strengths.length ? strengths : ["—"]).slice(0, 8).map((t, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400" />
                          <span>{t}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
                    <div className="text-sm font-semibold">Areas to Improve</div>
                    <div className="mt-3 space-y-2 text-sm text-white/75">
                      {(improvements.length ? improvements : ["—"]).slice(0, 8).map((t, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-300" />
                          <span>{t}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
                    <div className="text-sm font-semibold">Recommendations</div>
                    <div className="mt-3 space-y-2 text-sm text-white/75">
                      {(recommendations.length ? recommendations : ["—"]).slice(0, 8).map((t, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-sky-400" />
                          <span>{t}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Success footer */}
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-2xl bg-emerald-400/10 border border-emerald-400/15 flex items-center justify-center text-emerald-100">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div className="text-sm text-white/75">
                      Your results are saved. Export your PDF or copy the full Q&A.
                    </div>
                  </div>
                  <div className="text-xs text-white/55">
                    Tip: Re-run with higher difficulty to stress-test clarity and pace.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </AppBackground>
  );
}
